import React, { useState, useEffect, useCallback, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import axios from "axios";

const API = window.location.origin;

const COLUMNS = [
  { key: "row_num", label: "#", width: 40, frozen: true },
  { key: "booking_time", label: "Saat", width: 55, frozen: true },
  { key: "customer_name", label: "Musteri", width: 160, frozen: true },
  { key: "confirmation_code", label: "Kod", width: 100 },
  { key: "total_pax", label: "Pax", width: 45 },
  { key: "product_name", label: "Urun", width: 200 },
  { key: "hotel_name", label: "Otel", width: 130 },
  { key: "customer_phone", label: "Telefon", width: 120 },
  { key: "kalkis", label: "Kalkis", width: 80 },
  { key: "masa_no", label: "Masa", width: 70 },
  { key: "garson", label: "Garson", width: 100 },
  { key: "status", label: "Durum", width: 80 },
  { key: "tahsilat_sekli", label: "Tahsilat", width: 80 },
  { key: "tutar", label: "Tutar", width: 70 },
  { key: "op_notes", label: "Not", width: 150 },
  { key: "boat_name", label: "Tekne", width: 80 },
  { key: "adult_count", label: "Yet.", width: 40 },
  { key: "child_count", label: "Coc.", width: 40 },
];

const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 40;
const FROZEN_COUNT = 3;
const FROZEN_WIDTH = COLUMNS.slice(0, FROZEN_COUNT).reduce((s, c) => s + c.width, 0);
const SCROLLABLE_WIDTH = COLUMNS.slice(FROZEN_COUNT).reduce((s, c) => s + c.width, 0);

// Memoized cell renderer
const Cell = React.memo(({ value, col, isSelected, onClick }) => (
  <div onClick={onClick}
    style={{ width: col.width, minWidth: col.width, height: ROW_HEIGHT }}
    className={`flex items-center px-2 text-xs border-r border-gray-100 truncate cursor-default shrink-0
      ${isSelected ? "bg-blue-50 outline outline-2 outline-blue-500 -outline-offset-2 z-20" : "hover:bg-gray-50"}
      ${col.key === "row_num" ? "text-gray-400 text-[10px] justify-center font-mono bg-gray-50/50" : "text-gray-800"}`}>
    {col.key === "total_pax" ? <b>{value || 0}</b> :
     col.key === "status" ? (
       <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
         value === "check_in" || value === "geldi" ? "bg-green-100 text-green-700" :
         value === "cancelled" || value === "iptal" ? "bg-red-100 text-red-600" :
         value === "no_show" || value === "gelmedi" ? "bg-amber-100 text-amber-700" :
         "bg-gray-100 text-gray-600"
       }`}>{value || "bekliyor"}</span>
     ) :
     col.key === "customer_name" ? <span className="font-medium truncate">{value || "-"}</span> :
     <span className="truncate">{value ?? "-"}</span>}
  </div>
));

export default function GridProto() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCell, setSelectedCell] = useState(null);
  const [fps, setFps] = useState(0);

  const scrollContainerRef = useRef(null);
  const fpsRef = useRef({ frames: 0, lastTime: performance.now() });

  const token = localStorage.getItem("auth_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // FPS counter
  useEffect(() => {
    let raf;
    const tick = () => {
      fpsRef.current.frames++;
      const now = performance.now();
      if (now - fpsRef.current.lastTime >= 1000) {
        setFps(fpsRef.current.frames);
        fpsRef.current.frames = 0;
        fpsRef.current.lastTime = now;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Load data
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/sheet/2026-04-14`, { headers });
        const d = res.data;
        const boatMap = {};
        (d.boats || []).forEach(b => { boatMap[b.id] = b.name; });

        let realRows = (d.rows || []).map((r, i) => ({
          ...r, row_num: i + 1, boat_name: boatMap[r.boat_id] || "-",
        }));

        // Pad to 320+ rows
        const padCount = Math.max(0, 320 - realRows.length);
        for (let i = 0; i < padCount; i++) {
          const src = realRows[i % realRows.length];
          realRows.push({
            ...src, id: 90000 + i, booking_id: 90000 + i,
            row_num: realRows.length + 1,
            customer_name: `SIM-${i} ${src.customer_name?.split(" ")[0] || "Test"}`,
          });
        }
        setRows(realRows);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  // Virtualizer — only rows
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  // Keyboard nav
  const handleKeyDown = useCallback((e) => {
    if (!selectedCell) return;
    let { rowIdx, colIdx } = selectedCell;
    switch (e.key) {
      case "ArrowDown": e.preventDefault(); rowIdx = Math.min(rows.length - 1, rowIdx + 1); break;
      case "ArrowUp": e.preventDefault(); rowIdx = Math.max(0, rowIdx - 1); break;
      case "ArrowRight": case "Tab": e.preventDefault(); colIdx = Math.min(COLUMNS.length - 1, colIdx + 1); break;
      case "ArrowLeft": e.preventDefault(); colIdx = Math.max(0, colIdx - 1); break;
      case "Enter": e.preventDefault(); rowIdx = Math.min(rows.length - 1, rowIdx + 1); break;
      case "Escape": setSelectedCell(null); return;
      default: return;
    }
    setSelectedCell({ rowIdx, colIdx });
    rowVirtualizer.scrollToIndex(rowIdx, { align: "auto" });
  }, [selectedCell, rows.length, rowVirtualizer]);

  if (loading) return <div className="p-8 text-center text-gray-500">Yukleniyor...</div>;

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();

  return (
    <div className="min-h-screen bg-gray-100 p-4" data-testid="grid-proto" onKeyDown={handleKeyDown} tabIndex={0}>
      {/* Debug bar */}
      <div className="bg-white border rounded-lg p-2 mb-3 flex items-center gap-4 text-xs flex-wrap">
        <b className="text-gray-800">Grid Proto</b>
        <span>Satirlar: <b className="text-blue-600">{rows.length}</b></span>
        <span>Sutunlar: <b className="text-blue-600">{COLUMNS.length}</b></span>
        <span>Hucreler: <b className="text-blue-600">{rows.length * COLUMNS.length}</b></span>
        <span>DOM satirlar: <b className="text-green-600">{virtualRows.length}</b></span>
        <span className={`font-bold ${fps >= 50 ? "text-green-600" : fps >= 30 ? "text-amber-500" : "text-red-600"}`}>{fps} FPS</span>
        <span className="text-gray-400">Secili: {selectedCell ? `[${selectedCell.rowIdx}, ${selectedCell.colIdx}]` : "-"}</span>
      </div>

      {/* Grid */}
      <div className="bg-white border rounded-lg shadow-sm overflow-hidden" style={{ position: "relative" }}>
        {/* Scroll container — handles BOTH vertical (virtualized) and horizontal scroll */}
        <div ref={scrollContainerRef}
          style={{ height: "calc(100vh - 140px)", overflow: "auto", position: "relative" }}
          data-testid="grid-scroll">

          {/* Full-size inner to create scroll area */}
          <div style={{ width: FROZEN_WIDTH + SCROLLABLE_WIDTH, minHeight: totalHeight + HEADER_HEIGHT, position: "relative" }}>

            {/* HEADER ROW — sticky top */}
            <div style={{ position: "sticky", top: 0, zIndex: 25, height: HEADER_HEIGHT, display: "flex", backgroundColor: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
              {/* Frozen header cells */}
              <div style={{ position: "sticky", left: 0, zIndex: 30, display: "flex", backgroundColor: "#F8FAFC" }}>
                {COLUMNS.slice(0, FROZEN_COUNT).map(col => (
                  <div key={col.key} style={{ width: col.width, minWidth: col.width, height: HEADER_HEIGHT }}
                    className="flex items-center px-2 text-[11px] font-bold text-gray-600 uppercase tracking-wide border-r border-gray-200 shrink-0">
                    {col.label}
                  </div>
                ))}
              </div>
              {/* Scrollable header cells */}
              {COLUMNS.slice(FROZEN_COUNT).map(col => (
                <div key={col.key} style={{ width: col.width, minWidth: col.width, height: HEADER_HEIGHT }}
                  className="flex items-center px-2 text-[11px] font-bold text-gray-600 uppercase tracking-wide border-r border-gray-200 shrink-0">
                  {col.label}
                </div>
              ))}
            </div>

            {/* VIRTUAL ROWS */}
            <div style={{ position: "relative", height: totalHeight }}>
              {virtualRows.map(vRow => {
                const row = rows[vRow.index];
                const isSimulated = vRow.index >= 257;
                return (
                  <div key={vRow.key}
                    style={{ position: "absolute", top: vRow.start, left: 0, height: ROW_HEIGHT, display: "flex", width: FROZEN_WIDTH + SCROLLABLE_WIDTH }}
                    className={`border-b border-gray-100 ${isSimulated ? "bg-yellow-50/40" : ""}`}>
                    {/* Frozen cells — sticky left */}
                    <div style={{ position: "sticky", left: 0, zIndex: 10, display: "flex", backgroundColor: isSimulated ? "#FEFCE8" : "white" }}>
                      {COLUMNS.slice(0, FROZEN_COUNT).map((col, ci) => (
                        <Cell key={col.key} col={col} value={row[col.key]}
                          isSelected={selectedCell?.rowIdx === vRow.index && selectedCell?.colIdx === ci}
                          onClick={() => setSelectedCell({ rowIdx: vRow.index, colIdx: ci })} />
                      ))}
                    </div>
                    {/* Scrollable cells */}
                    {COLUMNS.slice(FROZEN_COUNT).map((col, ci) => (
                      <Cell key={col.key} col={col} value={row[col.key]}
                        isSelected={selectedCell?.rowIdx === vRow.index && selectedCell?.colIdx === FROZEN_COUNT + ci}
                        onClick={() => setSelectedCell({ rowIdx: vRow.index, colIdx: FROZEN_COUNT + ci })} />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 text-[10px] text-gray-400 text-center">
        Ok/Tab/Enter ile gezin. Ilk 3 sutun donuk. Yatay kaydirmayi test edin. Sari satirlar simule (258-320).
      </div>
    </div>
  );
}
